import { EMPTY, forkJoin, from, of, zip } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import get from 'lodash.get';
import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../store/global-selectors';
import { ModalService } from 'ui-components/services/modal-service';
import * as PostagePurchaseActions from '../../store/postage-purchase/postage-purchase.actions';
import * as PostagePurchaseApi from './postage-purchase.api';
import * as WalletApi from '../../wallet/utilities/api';
import { costReviewSubject } from '../../components/modals/cost-review-modal';
import { WorkflowSteps, trackWorkflow } from '../../tracking/use-workflow-tracking';
import { Workflows } from '../../tracking/tracking-constants';
import { getAccountInformation } from '../account-information/account-information.actions';

const successAction = { success: true };
const success = () => of(successAction);

const postageRequests = (providers, userId, jobGuid) => {
  return !providers.length
    ? success()
    : forkJoin(
        ...providers.map((p) => {
          return PostagePurchaseApi.postagePurchase(p.purchaseAmount, userId, p.sellerLabelProviderId);
        }),
      ).pipe(
        mergeMap(success),
        catchError((e) => {
          const requestData = JSON.parse(get(e, 'config.data'));
          const sellerLabelProviderId = get(requestData, 'sellerLabelProviderId');
          const errors = get(e, 'response.data.Errors', emptyArray)
            .map((msg) => get(msg, 'Message'))
            .join(', ');
          return of(PostagePurchaseActions.postagePurchaseFailure(sellerLabelProviderId, jobGuid, errors));
        }),
      );
};

const insuranceRequest = (insurancePurchaseAmount, jobGuid) => {
  return !insurancePurchaseAmount
    ? success()
    : PostagePurchaseApi.insurancePurchase(insurancePurchaseAmount).pipe(
        mergeMap(success),
        catchError((e) => {
          const errors = get(e, 'response.data');
          const errorMessage = get(errors, 'Errors.0.Message');
          return of(PostagePurchaseActions.postageAndInsurancePurchaseFailure(jobGuid, errorMessage));
        }),
      );
};

const walletRequest = (wallets, jobGuid, tracking, onAddFundsComplete, closeModal) => {
  return !wallets.length
    ? success()
    : forkJoin(
        ...wallets.map((w) => {
          return from(WalletApi.fundAccount(w.walletId, w.walletPurchaseAmount));
        }),
      ).pipe(
        mergeMap(() => {
          onAddFundsComplete();
          trackWorkflow(Workflows.ADD_FUNDS_FLOW, WorkflowSteps.COMPLETE, {
            amount: wallets[0].walletPurchaseAmount.value,
            currency: wallets[0].walletPurchaseAmount.code,
          });
          closeModal();
          return of([successAction, getAccountInformation()]);
        }),
        catchError((e) => {
          const errorMsg = e?.response?.data?.Errors[0]?.Message;
          trackWorkflow(Workflows.ADD_FUNDS_FLOW, WorkflowSteps.ERROR, {
            error: e,
            amount: wallets[0].walletPurchaseAmount.value,
            currency: wallets[0].walletPurchaseAmount.code,
          });
          return of([PostagePurchaseActions.postageAndInsurancePurchaseFailure(jobGuid, errorMsg), getAccountInformation()]);
        }),
      );
};

export const purchasePostageInsuranceWalletFundsRequest = (action$, { getState }) => {
  return action$.pipe(
    ofType(PostagePurchaseActions.POSTAGE_AND_INSURANCE_PURCHASE_REQUEST),
    map((action) => {
      const providers = action.providers || [];
      return {
        ...action,
        // filter out insurances from the purchase postage call
        providers: providers.filter((p) => p && p.purchaseAmount > 0 && !!p.sellerLabelProviderId),
      };
    }),
    mergeMap(({ jobGuid, providers, insurances = [], wallets, onAddFundsComplete, tracking, closeModal }) => {
      const userId = Global.selectUserCurrentUserId(getState());
      const insurancePurchaseAmount = get(insurances, '0.purchaseAmount');

      return zip(
        postageRequests(providers, userId, jobGuid),
        insuranceRequest(insurancePurchaseAmount, jobGuid),
        walletRequest(wallets, jobGuid, tracking, onAddFundsComplete, closeModal),
      ).pipe(
        map((responses) => {
          const [postageRes, insuranceRes, walletRes] = responses; // walletRes here is an array of returned actions from the walletRequest observable
          const walletSuccess = Array.isArray(walletRes) ? walletRes[0].success : walletRes.success;
          const allWalletResponses = Array.isArray(walletRes) ? walletRes : [walletRes];
          if (postageRes.success && insuranceRes.success && walletSuccess) {
            costReviewSubject.next({
              costReviewErrorMessage: emptyArray,
            });
            return;
          }
          return [postageRes, insuranceRes, ...allWalletResponses].filter((res) => res && res.type);
        }),
        mergeMap((errorActions) => {
          if (errorActions) {
            costReviewSubject.next({
              costReviewErrorMessage: errorActions,
            });
            // TODO: figure out how to handle the case where one call succeeds and
            // the other fails. We have never handled this very well.
            return of(...errorActions);
          }
          return of(PostagePurchaseActions.postageAndInsurancePurchaseSuccess(jobGuid));
        }),
      );
    }),
  );
};

export const handlePostagePurchaseSuccessAndPostagePurchaseDeferred = (action$) => {
  return action$.pipe(
    ofType(PostagePurchaseActions.POSTAGE_PURCHASE_DEFERRED),
    mergeMap((action) => {
      const { jobGuid } = action;
      const insurances = get(action, 'costReviewData.insuranceCosts', emptyArray);
      const purchaseAmount = get(insurances, '0.purchaseAmount');

      if (!purchaseAmount) {
        return of(PostagePurchaseActions.postageAndInsurancePurchaseSuccess(jobGuid));
      }
      return EMPTY;
    }),
  );
};

export const closeOrderDetailsModalOnBillingInfoLinkClick = (action$, { getState }) => {
  return action$.pipe(
    ofType(PostagePurchaseActions.CLOSE_ORDER_DETAILS_MODAL_ON_BILLING_INFO_LINK_CLICK),
    mergeMap(() => {
      const orderDetailsModalId = Global.selectOrderDetailsModalId(getState());
      ModalService.closeModalById(orderDetailsModalId);
      return EMPTY;
    }),
  );
};
